<template>
  <div class="new-release-uploader">
    <div class="new-release-uploader__description">
      {{ $t('APP.RELEASES.ADD_APK_OR_AAB') }}
    </div>

    <DroppableResumableUploader
      ref="uploader"
      v-loading="loading"
      v-bind="$attrs"
      class="new-release-uploader__uploader"
      multiple
      :accept="validFileExtensions"
      :files-filter-function="normalizeSelectedFiles"
      :upload-url="getFileUploadUrl"
      @upload-done="handleResponses"
    />

    <ElAlert
      v-for="(failure, name) in fileErrors"
      :key="name"
      :title="$t('APP.RELEASES.PACKAGE_ERROR', { name })"
      :closable="false"
      class="new-release-uploader__error"
      type="error"
      show-icon
    >
      <p>{{ failure.message }}</p>

      <ul>
        <li v-for="errorDetail in failure.details" :key="errorDetail">
          {{ errorDetail }}
        </li>
      </ul>
    </ElAlert>
  </div>
</template>

<script>
import packageName from '@/mixins/packageName';
import {
  APK_UPLOAD_PATH,
  AAB_UPLOAD_PATH,
} from '@/api/file-uploader/endpoints';
import DroppableResumableUploader from '@@/common/file-uploaders/DroppableResumableUploader';
import { Release } from '@/api/api';
import { UPDATE_UNCOMMITTED_RELEASE } from '@/store/types';
import { mapActions } from 'vuex';

export default {
  name: 'ReleaseFileUploader',

  components: {
    DroppableResumableUploader,
  },

  mixins: [packageName],

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    releaseId: {
      type: Number,
      required: true,
    },
    canUploadBundle: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      loading: false,
      fileErrors: {},
    };
  },

  computed: {
    validFileExtensions() {
      return this.canUploadBundle ? '.aab,.apk' : '.apk';
    },
  },

  methods: {
    ...mapActions({
      updateUncommittedRelease: UPDATE_UNCOMMITTED_RELEASE,
    }),

    normalizeSelectedFiles(files = []) {
      this.fileErrors = {};
      const isAAB = ({ name }) => name.endsWith('.aab');
      const isAPK = ({ name }) => name.endsWith('.apk');

      const aabFile = files.find(isAAB);
      if (aabFile) return [aabFile];
      return files.filter(isAPK);
    },

    handleResponses(responses) {
      this.fileErrors = {};
      this.loading = true;

      const promises = responses.map(({ apk, aab }) => {
        if (aab) return this.addBundleToRelease(aab);
        return this.addPackageToRelease(apk);
      });

      Promise.allSettled(promises).then(() => {
        this.loading = false;
        this.$refs.uploader?.resetAndCancel();
      });
    },

    getFileUploadUrl({ name }) {
      const fileExtension = name
        .split('.')
        .pop()
        .toLowerCase();

      const extensionUploadEndpoints = {
        apk: APK_UPLOAD_PATH,
        aab: AAB_UPLOAD_PATH,
      };

      return extensionUploadEndpoints[fileExtension];
    },

    addPackageToRelease(apk) {
      return Release.addPackageToRelease({
        apk,
        releaseId: this.releaseId,
        packageName: this.packageName,
      })
        .then(({ package: packageItem, message }) => {
          this.$message.success(message);
          this.$emit('add-package', packageItem);
        })
        .catch(({ message, errors = [] }) => {
          this.fileErrors[apk.versionCode] = { message, details: errors };
        });
    },

    addBundleToRelease(bundleToken) {
      return Release.addBundleToRelease({
        token: bundleToken,
        releaseId: this.releaseId,
        packageName: this.packageName,
      })
        .then(({ bundle }) => {
          this.updateUncommittedRelease({ bundle });
        })
        .catch(({ message, errors = [] }) => {
          this.fileErrors[bundleToken] = { message, details: errors };
        });
    },
  },
};
</script>

<style lang="scss">
.new-release-uploader {
  &__error {
    margin-bottom: $global-margin;
  }

  &__description {
    margin-bottom: $global-margin;
  }

  &__uploader {
    margin-bottom: $global-margin;
  }
}
</style>
